import {
  APIFilterDropdown,
  FilterDropdown,
  FilterText,
  FilterTextChangeValue,
} from "../filterDropdown";
import { Dropdown, MultiPhaseDropdown } from "../form";
import Icon from "../icon/Icon";
import { TableHeaderType } from "./types";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { uniqueId } from "lodash";
import { ComponentProps, useMemo } from "react";
import { BetterPopover } from "../popover";
import { PopoverContent } from "@altimateai/lego";
import { IconFilter2 } from "@tabler/icons-react";

interface TableFilterProps {
  filter: TableHeaderType["filter"];
  label: string;
  resetPage?: () => void;
  popoverContentProps?: ComponentProps<typeof PopoverContent>;
}

/**
 * @deprecated
 * will be replaced by filterssection once all tables are migrated to new filter format
 * @param param0
 * @returns
 */
const TableFilter = ({ filter, label, resetPage, popoverContentProps = {} }: TableFilterProps) => {
  const { updateSearchParamsByKey } = useFiltersBySearchParams();
  const filterId = useMemo(() => uniqueId("filter-"), []);

  const setChangedState = (currentState: string | string[] | FilterTextChangeValue) => {
    if (filter?.searchKey && (typeof currentState !== "object" || Array.isArray(currentState))) {
      updateSearchParamsByKey({ [filter.searchKey]: currentState });
    }
    // Each rendering branch guarantees the correct runtime type for onChange.
    // Cast is acceptable here since TableFilter is @deprecated infrastructure.
    (filter?.onChange as ((v: string | string[] | FilterTextChangeValue) => void) | undefined)?.(
      currentState
    );
    resetPage?.();
  };
  if (!filter) return null;

  return (
    <>
      {filter.filterType === "text" && (
        <FilterText
          label={filter.label}
          onChange={value => setChangedState(value)}
          value={filter.value}
          placeHolder={filter.placeHolder}
          filterMode={
            filter.filterMode && typeof filter.filterMode === "object"
              ? filter.filterMode
              : undefined
          }
        >
          <button type="button" aria-label={`Filter ${label}`}>
            <Icon>
              <IconFilter2 className="al-size-4" />
            </Icon>
          </button>
        </FilterText>
      )}
      {filter.filterType === "dropdown" && filter.options && filter.options.length > 0 && (
        <FilterDropdown
          label={label}
          value={Array.isArray(filter.value) ? filter.value : filter.value ? [filter.value] : []}
          options={filter.options || []}
          onChange={setChangedState}
          searchBy={
            filter.searchBy === "label" || filter.searchBy === "value" ? filter.searchBy : undefined
          }
          popoverContentProps={filter.popoverContentProps || popoverContentProps}
        >
          <button type="button" aria-label={`Filter ${label}`}>
            <Icon>
              <IconFilter2 className="al-size-4" />
            </Icon>
          </button>
        </FilterDropdown>
      )}
      {filter.filterType === "dropdown_single" && (
        <Dropdown
          id={filter?.id}
          value={filter?.value}
          options={filter?.options || []}
          onChange={filter?.onChange}
          label={filter?.label}
          wrapperClass=""
          showDivider={false}
          popoverContentProps={popoverContentProps}
        >
          <button type="button" aria-label={`Filter ${label}`}>
            <Icon>
              <IconFilter2 className="al-size-4" />
            </Icon>
          </button>
        </Dropdown>
      )}
      {filter.filterType === "multiDropdown" && (
        <MultiPhaseDropdown
          id="multi-phase-dropdown"
          options={filter.options || []}
          placement={"right"}
          showDivider
          label={filter.label}
          openMultiselectOnHover={filter.multiSelectFilterOpenOnHover}
          openMultiselectOnFirstHover={filter.multiSelectFilterOpenOnFirstHover}
        >
          <button type="button" aria-label={`Filter ${label}`}>
            <Icon>
              <IconFilter2 className="al-size-4" />
            </Icon>
          </button>
        </MultiPhaseDropdown>
      )}
      {filter.filterType === "apiDropdown" && (
        <BetterPopover
          id={filterId}
          popoverContentProps={popoverContentProps}
          trigger={
            <button type="button" aria-label={`Filter ${label}`}>
              <Icon>
                <IconFilter2 className="al-size-4" />
              </Icon>
            </button>
          }
          render={({ close }) => (
            <APIFilterDropdown
              id={filter.id || "api-filter"}
              label={label}
              value={Array.isArray(filter.value) ? filter.value : []}
              apiFunction={filter.apiFunction!}
              pageSize={filter?.pageSize}
              extractorFunction={filter.extractorFunction}
              onChange={(value: string[]) => {
                setChangedState(value);
                close();
              }}
              seeMore={filter.seeMore}
              searchKey={filter.searchKey}
            />
          )}
        />
      )}
    </>
  );
};

export default TableFilter;
